import {
  termsService,
  type TermsCreateRequest,
  type TermsType,
  type TermsUpdateRequest,
} from '@/entities/terms/api/terms.api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const termsKeys = {
  all: ['terms'] as const,
  types: () => [...termsKeys.all, 'types'] as const,
  detail: (termsId: number) => [...termsKeys.all, 'detail', termsId] as const,
  versions: (type: TermsType | undefined) =>
    [...termsKeys.all, 'versions', type] as const,
};

/** 약관 종류 5종 + 각 종류의 현재·시행 예정 버전 */
export const useTermsTypes = () =>
  useQuery({
    queryKey: termsKeys.types(),
    queryFn: () => termsService.getTypes(),
  });

export const useTermsDetail = (termsId: number) =>
  useQuery({
    queryKey: termsKeys.detail(termsId),
    queryFn: () => termsService.getDetail(termsId),
    enabled: termsId > 0,
  });

export const useTermsVersions = (type: TermsType | undefined, enabled = true) =>
  useQuery({
    queryKey: termsKeys.versions(type),
    queryFn: () => termsService.getVersions(type as TermsType),
    enabled: Boolean(type) && enabled,
  });

export const useCreateTerms = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TermsCreateRequest) => termsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termsKeys.all });
    },
  });
};

export const useUpdateTerms = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      termsId,
      data,
    }: {
      termsId: number;
      data: TermsUpdateRequest;
    }) => termsService.update(termsId, data),
    onSuccess: (_, { termsId }) => {
      queryClient.invalidateQueries({ queryKey: termsKeys.detail(termsId) });
      queryClient.invalidateQueries({ queryKey: termsKeys.all });
    },
  });
};

/**
 * 버전 삭제 — 현재 시행 버전을 지우면 이전 시행 버전이 다시 current가 되므로
 * 목록·이력 전체를 다시 불러온다. 삭제된 상세 캐시는 refetch(404)되지 않게 제거한다.
 */
export const useDeleteTerms = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (termsId: number) => termsService.delete(termsId),
    onSuccess: (_, termsId) => {
      queryClient.removeQueries({ queryKey: termsKeys.detail(termsId) });
      queryClient.invalidateQueries({ queryKey: termsKeys.all });
    },
  });
};
